import { Button } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import {
    AuditTrailModel,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    PositionModel, ReportType, YearDataModel, CustomerReportModel
} from '../../../../../Generated/Commodity-Management-Api';
import { useLoadingState } from '../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { StoneXDivider, StoneXLoading, StoneXRow } from '../../../../StoneX';
import StoneXMainPage from '../../../../StoneX/StoneXMainPage/StoneXMainPage';
import { UpdatePositionsModal } from '../UpdatePositionsPage/UpdatePositionsModal';
import BasicQueryForm from './BasicQueryForm';
import PositionTable from './PositionTable';
import PositionValidationModal from './PositionValidationModal';
import ViewAuditTrailModal from '../../AuditTrail/AuditTrailModal';
import PositionReportSettingsModal from './PositionReportSettingsModal';
import { deepMerge } from '../../../../../Helpers/ObjectHelper';
import { PositionReportColumn, PositionReportConfiguration, PositionReportUserOverride } from './Types';
import { GroupingOption } from '../../../../../Types/GridRow';

export default function ViewPositionsPage() {

    /*const customerReport = useApplicationSelector((state: RootState): CustomerReportModel =>
        state.commodityManagement.customerReports.find((x: any) => x.ReportType === ReportType.Position)!);*/

    const yearsLoadingState = useLoadingState();
    const auditLoadingState = useLoadingState();

    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);
    const customerReports = useApplicationSelector((state: RootState): CustomerReportModel[] => state.commodityManagement.customerReports);

    const customerReport = customerReports.find((x: any) => x.reportType === ReportType.Position)!;

    const [years, setYears] = useState<YearDataModel[]>([]);

    const postionsLoadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);

    const [editDealTicketModalOpen, setEditDealTicketModalOpen] = useState(false);
    const [editDealTicketId, setEditDealTicketId] = useState<number | undefined>();

    const [validationModalOpen, setValidationModalOpen] = useState(false);
    const [validationDealTicketId, setDealTicketValidationId] = useState<number | undefined>();

    const [auditTrailModalIsOpen, setAuditTrailModalIsOpen] = useState(false);
    const [AuditTrailModels, setAuditTrialChangeEvents] = useState<AuditTrailModel[]>([]);
    const [isConfiguringReport, setIsConfiguringReport] = useState<boolean>(false);
    const [customDataKeys, setCustomDataKeys] = useState<string[]>([]);


    const reportConfiguration = useMemo(() => JSON.parse(customerReport.configuration!) as PositionReportConfiguration, [customerReport]);
    const userOverrides = useMemo(() => customerReport.userOverrides ? JSON.parse(customerReport.userOverrides) as PositionReportUserOverride : undefined, [customerReport]);
    const reportSettings = useMemo(() => deepMerge(reportConfiguration, userOverrides), [reportConfiguration, userOverrides]);
    const columnsToShow = useMemo(getColumnsToShow, [reportSettings]);

    const [groupingOptions, setGroupingOptions] = useState<GroupingOption[]>([]);

    useEffect(() => {
        CommodityManagementApi.listPositionYears(yearsLoadingState.setLoadingState).then((q) => setYears(q.data.rows ?? []));
    }, []);

    function setPositionValidation(dealTicketId: number) {
        setValidationModalOpen(true);
        setDealTicketValidationId(dealTicketId);
    }

    function editDealTicket(dealTicketId?: number) {
        // This will work as add deal ticket if dealTicketId is undefined
        setEditDealTicketId(dealTicketId);
        setEditDealTicketModalOpen(true);
    }

    function getColumnsToShow() {
        return reportSettings.columns?.active;
    }

    async function onDealTicketSave(dealTicketId: number) {
        await CommodityManagementApi.listPositions(
            undefined,
            undefined, //mtmDate
            false, //include expiration dates
            false, //include market prices
            false, // don't use display units - use original units
            undefined, // no scenario market prices
            undefined, //commodityFilter,
            undefined, //locationFilter,
            undefined, //counterpartyFilter,
            undefined, //trade start date
            undefined, //trade end date
            undefined, // startDate.year,
            undefined, // startDate.month,
            undefined, //label (ignore)
            undefined, // endDate.year,   //get all forward positions
            undefined, // endDate.month,  //get all forward positions
            undefined, // label
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            dealTicketId, // filter by deal ticket id
        ).then((q) => {
            const newPositions = q.data.rows ?? [];
            setPositions(previous => {
                // Splice the saved rows back in where the deal ticket's rows already
                // were, so an edited position keeps its place in the table instead of
                // jumping to the bottom. A brand new deal ticket has no existing rows,
                // so it lands at the end.
                const firstIndex = previous.findIndex(p => p.dealTicketId === dealTicketId);

                // Drop the deal ticket's previous rows. Also drop anything matching a
                // returned positionId, in case the save moved a position onto a
                // different deal ticket - otherwise it would appear twice.
                const returnedPositionIds = new Set(newPositions.map(p => p.positionId));
                const remaining = previous.filter(
                    p => p.dealTicketId !== dealTicketId && !returnedPositionIds.has(p.positionId)
                );

                // The server was just queried for exactly this deal ticket, so its
                // response is authoritative. The rows used to be filtered against the
                // `years` list here, but that list holds fiscal years (see
                // ListPositionYearsService) while budgetDate.year is a calendar year -
                // so for any customer whose fiscal year doesn't start in January, the
                // saved rows were dropped and only reappeared after a refresh.
                const insertAt = firstIndex === -1 ? remaining.length : Math.min(firstIndex, remaining.length);

                return [
                    ...remaining.slice(0, insertAt),
                    ...newPositions,
                    ...remaining.slice(insertAt),
                ];
            });
            setEditDealTicketModalOpen(false);
        });
    }

    function closeAuditTrailModal() {
        setAuditTrailModalIsOpen(false);
        setAuditTrialChangeEvents([]);
    }

    function viewAuditTrail(positionId: number) {
        setAuditTrailModalIsOpen(true);
        CommodityManagementApi.getPositionAuditTrail(auditLoadingState.setLoadingState, positionId.toString()).then(q => {
            setAuditTrialChangeEvents(q.data.rows ?? []);
        });
    }

    function onDealTicketDelete(dealTicketId: number) {
        setPositions(previous => previous.filter(p => p.dealTicketId !== dealTicketId));
        setEditDealTicketModalOpen(false);
    }

    function onValidationUpdated(dealTicketId: number, validatedPositionIds: number[]) {
        setPositions(previous => previous.map(q => {
            if (q.dealTicketId === dealTicketId) {
                q.isValidated = !!validatedPositionIds.find((r) => r === q.positionId);
            }
            return q;
        }))
    }

    function updateGroupingOptions() {

        const groupingOptions: GroupingOption[] = ['Commodity', 'BudgetMonth'];

        if (customerSettings.positionsByLocation) {
            groupingOptions.push('BudgetingTier');
            groupingOptions.push('Tiers');
        }

        if (customerSettings.positionsByCounterparty) {
            groupingOptions.push('Counterparty');
        }

        setGroupingOptions(groupingOptions);
    }

    useEffect(updateGroupingOptions, []);
    function closeModal() {
        setIsConfiguringReport(false);
    }

    return (
        <StoneXMainPage>
            <StoneXRow align="baseline">
                <h1>Positions</h1>

                <span className="pull-right">
                    <Button onClick={() => editDealTicket()}>Add position</Button>
                    <Button className='pull-right' variant='outlined' onClick={() => setIsConfiguringReport(true)}>Configure Report</Button>
                </span>
            </StoneXRow>

            <BasicQueryForm
                customerSettings={customerSettings!}
                years={years}
                loadingStateSetter={postionsLoadingState.setLoadingState}
                onPositionsUpdated={setPositions}
            />

            <StoneXDivider />

            <PositionTable
                customerSettings={customerSettings}
                positions={positions}
                columnsToShow={columnsToShow}
                onEditPosition={editDealTicket}
                onSetPositionValidation={setPositionValidation}
                onViewAuditTrail={viewAuditTrail}
                onCustomDataKeysChange={setCustomDataKeys}
            />

            <PositionValidationModal
                open={validationModalOpen}
                dealTicketId={validationDealTicketId}
                onClose={() => setValidationModalOpen(false)}
                onValidationUpdated={onValidationUpdated}
            />

            <UpdatePositionsModal
                dealTicketId={editDealTicketId}
                additionalCustomDataKeys={customDataKeys}
                open={editDealTicketModalOpen}
                onClose={() => setEditDealTicketModalOpen(false)}
                onSave={onDealTicketSave}
                onDelete={onDealTicketDelete}
            />

            <ViewAuditTrailModal
                AuditTrailModels={AuditTrailModels}
                open={auditTrailModalIsOpen}
                isLoading={auditLoadingState.isLoading()}
                onClose={closeAuditTrailModal}
            />

            <PositionReportSettingsModal open={isConfiguringReport} customerReport={customerReport} groupingOptions={groupingOptions} onClose={closeModal} onSave={closeModal} />
        </StoneXMainPage>
    );
}